import React, { useEffect, useState } from 'react';
import { Container, Card, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import RelNegociosService from '~/Services/GES/RelNegociosService';
import FormFiltros from './components/FormFiltros';
import GridResultado from './components/GridResultado';
import BotoesExport from './components/BotoesExport';
import {
  filtrosIniciais,
  montarPayloadFiltro,
  validarFiltros,
  mensagemErro,
  TAMANHO_PAGINA,
} from './helpers';

// W-0595 — GES > Relatórios > Negócios > Lista de Negócios (read-only).
// Filtros CUMULATIVOS (AND no WHERE — NÃO single-select). Paginação SERVER-SIDE via /gerar.
// Export PDF/Excel server-side reusa os mesmos filtros (controller compartilha o request).
// Limite de aviso de volume (PRD §5.3 — "Volume alto").
const LIMITE_VOLUME = 1000;

const RelNegociosPage = () => {
  // Lookups.
  const [ges, setGes] = useState([]);
  const [ativos, setAtivos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [contas, setContas] = useState([]);
  const [carregandoContas, setCarregandoContas] = useState(false);

  // Filtros + resultado.
  const [filtros, setFiltros] = useState(filtrosIniciais());
  const [resultado, setResultado] = useState(null); // { itens, total_registros, pagina, tamanho_pagina }
  const [payloadGerado, setPayloadGerado] = useState(null); // filtro aplicado na última geração
  const [horaGerada, setHoraGerada] = useState(false); // mostrar_hora vigente no resultado
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  // Carrega GEs, Ativos e Usuários na montagem (anti-race com flag local).
  useEffect(() => {
    let ativo = true;
    RelNegociosService.listarGruposEmpresa()
      .then(res => {
        if (ativo) setGes(res || []);
      })
      .catch(err => {
        if (ativo) toast.error(mensagemErro(err, 'Erro ao carregar GEs'));
      });
    RelNegociosService.listarAtivos()
      .then(res => {
        if (ativo) setAtivos(res || []);
      })
      .catch(err => {
        if (ativo) toast.error(mensagemErro(err, 'Erro ao carregar ativos'));
      });
    RelNegociosService.listarUsuarios()
      .then(res => {
        if (ativo) setUsuarios(res || []);
      })
      .catch(err => {
        if (ativo) toast.error(mensagemErro(err, 'Erro ao carregar usuários'));
      });
    return () => {
      ativo = false;
    };
  }, []);

  // Carrega contas quando o GE muda (cascata com anti-race contra resposta stale).
  useEffect(() => {
    const ge = filtros.codigo_grupo_empresa;
    if (!ge) {
      setContas([]);
      return undefined;
    }
    let ativo = true;
    setCarregandoContas(true);
    RelNegociosService.listarContas(ge)
      .then(res => {
        if (ativo) setContas(res || []);
      })
      .catch(err => {
        if (ativo) {
          setContas([]);
          toast.error(mensagemErro(err, 'Erro ao carregar contas do GE'));
        }
      })
      .finally(() => {
        if (ativo) setCarregandoContas(false);
      });
    return () => {
      ativo = false;
    };
  }, [filtros.codigo_grupo_empresa]);

  // Busca uma página específica do relatório no backend (server-side).
  const buscarPagina = (payload, pagina, mostrarHora) => {
    setErro('');
    setCarregando(true);
    const corpo = { ...payload, pagina, tamanho_pagina: TAMANHO_PAGINA };
    return RelNegociosService.gerar(corpo)
      .then(res => {
        const resposta = res || {};
        setResultado(resposta);
        setPaginaAtual(resposta.pagina || pagina);
        setHoraGerada(mostrarHora);
        const totalRegistros = resposta.total_registros || 0;
        const itens = resposta.itens || [];
        if (totalRegistros === 0 && itens.length === 0) {
          setErro('Nenhum negócio encontrado para os filtros informados.');
        } else if (totalRegistros > LIMITE_VOLUME) {
          toast.info('Volume alto — paginação ativa.');
        }
      })
      .catch(err => {
        setResultado(null);
        const msg = mensagemErro(err, 'Erro ao gerar relatório.');
        setErro(msg);
        toast.error(msg);
      })
      .finally(() => {
        setCarregando(false);
      });
  };

  // Gerar = valida filtros e busca a 1ª página com os filtros atuais.
  const handleGerar = () => {
    const msgValidacao = validarFiltros(filtros);
    if (msgValidacao) {
      toast.error(msgValidacao);
      return;
    }
    const payload = montarPayloadFiltro(filtros);
    setPayloadGerado(payload);
    buscarPagina(payload, 1, payload.mostrar_hora);
  };

  // Troca de página: reusa o payload já gerado (não os filtros atuais, que podem ter mudado).
  const handleMudarPagina = pagina => {
    if (!payloadGerado || pagina === paginaAtual || carregando) return;
    buscarPagina(payloadGerado, pagina, horaGerada);
  };

  const totalRegistros = resultado ? resultado.total_registros || 0 : 0;
  const itens = resultado ? resultado.itens || [] : [];
  const totalPaginas = Math.max(
    1,
    Math.ceil(totalRegistros / TAMANHO_PAGINA)
  );

  return (
    <Container fluid className="py-3">
      <Header title="Lista de Negócios" />

      <Card className="mt-3">
        <Card.Body>
          <FormFiltros
            filtros={filtros}
            ges={ges}
            contas={contas}
            ativos={ativos}
            usuarios={usuarios}
            carregandoContas={carregandoContas}
            onChange={setFiltros}
            onGerar={handleGerar}
            carregando={carregando}
          />
        </Card.Body>
      </Card>

      {carregando && <Loading label="Gerando relatório..." />}

      {!carregando && erro && (
        <Alert variant="warning" className="mt-3">
          {erro}
        </Alert>
      )}

      {!carregando && resultado !== null && itens.length > 0 && (
        <div className="mt-3">
          <div className="d-flex justify-content-end mb-2">
            <BotoesExport payload={payloadGerado} desabilitado={false} />
          </div>

          <GridResultado
            itens={itens}
            mostrarHora={horaGerada}
            paginaAtual={paginaAtual}
            totalPaginas={totalPaginas}
            totalRegistros={totalRegistros}
            mudarPagina={handleMudarPagina}
          />
        </div>
      )}
    </Container>
  );
};

export default RelNegociosPage;
